import 'utils/translate-crash-guard';
import 'utils/dayjs-setup';
import { NavigationSync }      from 'components/App/NavigationSync';
import { StrictMode }          from 'react';
import { createRoot }          from 'react-dom/client';
import * as Sentry             from '@sentry/react';
import { BrowserRouter }       from 'react-router-dom';
import App                     from 'containers/App/App';
import { COLORS }              from 'styles/colors';
import ConfigProxy             from 'utils/ConfigProxy';
import { IntlProviderWrapper } from './IntlContext';
import { ConfigProvider }      from 'antd';
import fr_FR                   from 'antd/es/locale/fr_FR';
import { ErrorBoundary }       from '@sentry/react';
import { AppForm }             from 'components/App/AppForm';
import { App as AntdApp }      from 'antd';
import { formatMessageIntl }   from 'utils/messageIntl';

import 'sanitize.css/sanitize.css';

// Self-contained: rendered when a render error escapes to the top-level
// ErrorBoundary, which sits above IntlProviderWrapper/BrowserRouter/ConfigProvider,
// so none of those contexts are available here. formatMessageIntl reads
// directly from appStore rather than React context, so it still works here.
const ErrorFallback = () => (
  <div style={{ alignItems: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: '100vh', padding: 40, textAlign: 'center' }}>
    <h1>{formatMessageIntl('Erreur !')}</h1>
    <p>{formatMessageIntl('Merci de recharger la page. Si le problème persiste, contactez le support.')}</p>
    <button onClick={() => window.location.reload()} type="button">
      {formatMessageIntl('Recharger la page')}
    </button>
  </div>
);

if (ConfigProxy.get('SENTRY_DSN')) {
	Sentry.init({
		dsn: ConfigProxy.get('SENTRY_DSN'),
		environment: ConfigProxy.get('APP_ENV'),
		tracesSampleRate: ConfigProxy.get('APP_ENV') === 'prod' ? 0.01 : 1.0,

    // // utile pour la performance de l'application
    // integrations: [Sentry.browserTracingIntegration()],

    // pour voir l'erreur
    replaysSessionSampleRate: 0.01,
    replaysOnErrorSampleRate: 0.5,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],

    beforeSend(event, hint) {
      // modelx (ModelError/CollectionError) wraps the original Axios error in
      // `connectorError`, possibly several levels deep (e.g. "Error listing
      // models" wrapping "Error creating model" wrapping the Axios error) -
      // walk the chain to find the actual HTTP status.
      let error: any = hint.originalException;
      while (error) {
        if (error.response?.status === 401 || error.request?.status === 401) {
          return null;
        }

        // Attach the failed request to this event only (Sentry.setExtra in
        // the connector used to leak it to every following event).
        if (error.config) {
          event.extra = {
            ...event.extra,
            request_url: error.config.url,
            request_method: error.config.method,
            status: error.response?.status ?? error.request?.status,
          };
        }

        error = error.connectorError;
      }

      // Browser-extension noise (password managers/anti-fingerprinting
      // scripts poking at DOM nodes after React unmounts them): rejected
      // with a plain string, not an Error, and no app code in the picture.
      const original: any = hint.originalException;
      const originalMessage = typeof original === 'string' ? original : original?.message;
      if (originalMessage && /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/.test(originalMessage)) {
        return null;
      }

      return event;
    },
	});
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
		<ErrorBoundary fallback={ErrorFallback}>
			<IntlProviderWrapper>
				<BrowserRouter>
          <NavigationSync />
					<ConfigProvider
            locale={fr_FR}
            theme={{
              // components: {
              //   Modal: {
              //     footerBg: '#fafafa',
              //     headerBg: '#fafafa',
              //     titleFontSize: 18,
              //   },
              // },
              token: {
                colorError: COLORS.DANGER_RED,
                colorLink: COLORS.LINK,
                colorPrimary: COLORS.PRIMARY,
                colorSuccess: COLORS.SUCCESS_GREEN,

                // colorPrimary: '#707a96', // primary color for all components
                // colorLink: '#df5f2c', // link color
                // colorSuccess: '#52c41a', // success state color
                // colorError: '#f5222d', // error state color

                colorBorder: '#d9d9d9', // major border color
                colorText: 'rgba(0, 0, 0, .65)', // major text color
                colorTextDescription: 'rgba(0, 0, 0, .45)', // secondary text color
                colorTextDisabled: 'rgba(0, 0, 0, .25)', // disable state color
                colorTextHeading: 'rgba(0, 0, 0, .85)', // heading text color
                colorWarning: '#faad14', // warning state color

                borderRadius: 4, // major border radius
                boxShadow: '0 2px 8px rgba(0, 0, 0, .15)', // major shadow for layers
                fontSize: 14, // major text font size
              }
            }}
          >
            <AntdApp>
              <AppForm>
                <App />
              </AppForm>
            </AntdApp>
					</ConfigProvider>
				</BrowserRouter>
			</IntlProviderWrapper>
		</ErrorBoundary>
  </StrictMode>,
)